import { api } from "@/lib/api-client";
import dayjs from "dayjs";
import { useEffect, useRef, useState, type JSX } from "react";
import { useReactToPrint } from "react-to-print";
import { PostUserAppointmentList } from "../../../../server/src/types";
import { useCalendarStore } from "@/pages/Dashboard/Calendar/store/useCalendarStore";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import { getLocalizedName } from "@/utils";
import { FaRegComment } from "react-icons/fa";
import classes from "./ExportPdfModal.module.css";
import { Trans } from "@lingui/react/macro";
import { addToast, Divider } from "@heroui/react";
import { t } from "@lingui/core/macro";

type Appointment = PostUserAppointmentList["response"]["appointments"][0];

type GroupedAppointment = {
  date: string;
  appointments: Appointment[];
};

export const useExportPdfModal = () => {
  const ref = useRef<HTMLDivElement | null>(null);

  const { calendarMode } = useCalendarStore((state) => state);

  const [dateRange, setDateRange] = useState<(Date | null)[]>([
    dayjs().toDate(),
    dayjs().toDate(),
  ]);

  const { preferredLanguage } = usePreferredLanguageStore((state) => state);

  const { selectedLocationId } = useCalendarStore((state) => state);

  const [userIds, setUserIds] = useState<number[]>([]);
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [resourceIds, setResourceIds] = useState<number[]>([]);

  const [biggerRowSize, setBiggerRowSize] = useState<boolean>(false);

  const [contentToPrint, setContentToPrint] = useState<JSX.Element | null>(
    null,
  );

  const [modalOpened, setModalOpened] = useState(false);

  const { refetch } = api.appointments.useUserAppointmentList(
    {
      dateRange: dateRange
        .filter((date) => date !== null)
        .map((date) => dayjs(date).utc(true).format("YYYY-MM-DD")),
      userIds: calendarMode === "users" ? userIds : undefined,
      serviceIds,
      resourceIds: calendarMode === "resources" ? resourceIds : undefined,
      locationId: selectedLocationId,
    },
    true,
  );

  const { data: usersServicesData } =
    api.appointments.useUsersServicesForAppointments();

  const print = useReactToPrint({
    contentRef: ref,
    documentTitle: `LimeBooking - ${
      dayjs(dateRange[0]).format("DD.MM.YY") +
      (dayjs(dateRange[0]).isSame(dateRange[1], "day")
        ? ""
        : "-" + dayjs(dateRange[1]).format("DD.MM.YY"))
    }`,
  });

  useEffect(() => {
    if (!usersServicesData) return;

    setServiceIds(
      usersServicesData.services.map((service) => service.serviceId),
    );

    setUserIds(usersServicesData.users.map((user) => user.userId));

    setResourceIds(usersServicesData.resources.map((resource) => resource.id));
  }, [usersServicesData]);

  useEffect(() => {
    if (!contentToPrint) return;

    const container = document.getElementById("print-content");

    container?.style.setProperty("display", "block");
    print();
    container?.style.setProperty("display", "none");

    setContentToPrint(null);
    setModalOpened(false);
  }, [contentToPrint, print]);

  const handleExport = async () => {
    if (!ref) return;

    const response = await refetch();

    if (response.isError || !response.data) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri izvozu`,
        color: "danger",
      });
      return;
    }

    const container = document.getElementById("print-content");
    if (!container) return;

    const { data } = response;

    const { appointments } = data;

    const groupedAppointments: GroupedAppointment[] = appointments.reduce(
      (acc: GroupedAppointment[], curr) => {
        const formattedDate = dayjs(curr.startTimeLocal).format("DD. MM. YYYY");

        // Find the existing date entry
        const existingDateEntry = acc.find(
          (entry) => entry.date === formattedDate,
        );

        if (existingDateEntry && existingDateEntry.appointments) {
          // If date entry exists, push the current appointment to its appointments array
          existingDateEntry.appointments.push(curr);
        } else {
          // If date entry doesn't exist, create a new one and initialize its appointments array
          acc.push({
            date: formattedDate,
            appointments: [curr],
          });
        }

        return acc;
      },
      [],
    );

    const defaultLanguage =
      preferredLanguage.userPreferredLanguage ||
      preferredLanguage.clientPreferredLanguage ||
      "sl";

    const content = (
      <table className="w-full">
        <tbody>
          <tr>
            <th
              colSpan={12}
              className={classes["table-data"]}
              style={{ textAlign: "left" }}
            >
              {dayjs(dateRange[0]).format("DD. MM.")} -{" "}
              {dayjs(dateRange[1]).format("DD. MM.")}
            </th>
          </tr>
          {groupedAppointments.map((groupedAppointment) => (
            <>
              <tr style={{ background: "#F5F5F5", width: "100%" }}>
                <td colSpan={12} className={classes["table-data"]}>
                  <p className="text-xs">{groupedAppointment.date}</p>
                </td>
              </tr>
              {groupedAppointment.appointments.map((appointment) => {
                if (!appointment.startTimeLocal) return null;

                return (
                  <>
                    <tr>
                      <td
                        className={classes["table-data"]}
                        style={{ width: "7ch" }}
                      >
                        <p className="text-[9px]">
                          {dayjs(appointment.startTimeLocal).format("HH:mm")}
                        </p>
                      </td>
                      <td className={classes["table-data"]}>
                        <p className="text-[9px]">
                          {`${appointment.customer?.name} ${appointment.customer?.lastName}`}
                        </p>
                      </td>
                      <td className={classes["table-data"]}>
                        <p className="text-[9px]">
                          {appointment.appointment.service.nameLocalized.find(
                            (nl) => nl.language === defaultLanguage,
                          )?.name ||
                            appointment.appointment.service.nameLocalized[0]
                              .name}
                        </p>
                      </td>
                      <td className={classes["table-data"]}>
                        <p className="text-[9px]">
                          {appointment.customer?.email}
                        </p>
                      </td>
                      <td className={classes["table-data"]}>
                        <p className="text-[9px]">
                          {appointment.customer?.gsm}
                        </p>
                      </td>
                      <td className={classes["table-data"]}>
                        <p className="text-[9px]">
                          {appointment.appointment.appointmentUsers
                            .map((u) => `${u.user.name} ${u.user.lastName}`)
                            .join(", ")}
                        </p>
                      </td>
                      {appointment.appointment.appointmentResources.length >
                        0 && (
                        <td className={classes["table-data"]}>
                          <p className="text-[9px]">
                            {appointment.appointment.appointmentResources
                              .map((r) => r.resource.label)
                              .join(", ")}
                          </p>
                        </td>
                      )}

                      <td className={classes["table-data"]}>
                        <p className="text-[9px]">
                          <Trans>št. oseb: </Trans>
                          {appointment.attendance}
                        </p>
                      </td>
                    </tr>
                    {appointment.comment && (
                      <tr>
                        <td colSpan={1}>
                          <p className="mx-2 text-center text-[9px]">
                            <FaRegComment />
                          </p>
                        </td>
                        <td colSpan={11}>
                          <p className="w-full text-[9px]">
                            {appointment.comment}
                          </p>
                        </td>
                      </tr>
                    )}
                    {/** EXTRA FIELDS */}
                    {Array.isArray(appointment.extraDetails) &&
                      appointment.extraDetails.length > 0 && (
                        <tr>
                          <td colSpan={2}>
                            {appointment.extraDetails.map((detail) =>
                              renderExtraField(detail),
                            )}
                          </td>
                        </tr>
                      )}

                    {biggerRowSize && (
                      <tr>
                        <td
                          style={{
                            paddingBottom: "70px",
                          }}
                        ></td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan={12}>
                        <Divider className="mt-1" />
                      </td>
                    </tr>
                  </>
                );
              })}
            </>
          ))}
        </tbody>
      </table>
    );

    setContentToPrint(content);
  };

  const renderExtraField = (detail: Appointment["extraDetails"][number]) => {
    const renderValue = () => {
      if (detail.type === "radio" || detail.type === "select") {
        return (
          getLocalizedName(
            Object.entries(detail.value).map(([language, name]) => ({
              language,
              name,
            })),
          ) || "/"
        );
      }

      if (detail.type === "checkbox" || detail.type === "multi-select") {
        return detail.value
          .map(
            (value) =>
              getLocalizedName(
                Object.entries(value).map(([language, name]) => ({
                  language,
                  name,
                })),
              ) || "/",
          )
          .join(", ");
      }

      return detail.value.toString() || "/";
    };

    return (
      <div className="flex gap-1">
        <p className="w-full text-xs">
          {getLocalizedName(
            Object.entries(detail.label).map(([language, name]) => ({
              language,
              name,
            })),
          )}
        </p>
        <p className="w-full text-xs">{renderValue()}</p>
      </div>
    );
  };

  const selectAllUsers = () => {
    if (!usersServicesData) return;
    if (userIds.length === usersServicesData.users.length) {
      setUserIds([]);
    } else {
      setUserIds(
        usersServicesData.users.map((user: { userId: number }) => user.userId),
      );
    }
  };

  const selectAllServices = () => {
    if (!usersServicesData) return;
    if (serviceIds.length === usersServicesData.services.length) {
      setServiceIds([]);
    } else {
      setServiceIds(
        usersServicesData.services.map(
          (service: { serviceId: number }) => service.serviceId,
        ),
      );
    }
  };

  const selectAllResources = () => {
    if (!usersServicesData) return;
    if (resourceIds.length === usersServicesData.resources.length) {
      setResourceIds([]);
    } else {
      setResourceIds(
        usersServicesData.resources.map(
          (resource: { id: number }) => resource.id,
        ),
      );
    }
  };

  const toggleServiceSelection = (serviceId: number) => {
    if (serviceIds.includes(serviceId)) {
      setServiceIds((prev) => prev.filter((id) => id !== serviceId));
    } else {
      setServiceIds([...serviceIds, serviceId]);
    }
  };

  const toggleResourceSelection = (resourceId: number) => {
    if (resourceIds.includes(resourceId)) {
      setResourceIds((prev) => prev.filter((id) => id !== resourceId));
    } else {
      setResourceIds([...resourceIds, resourceId]);
    }
  };

  const toggleUserSelection = (userId: number) => {
    if (userIds.includes(userId)) {
      setUserIds((prev) => prev.filter((id) => id !== userId));
    } else {
      setUserIds([...userIds, userId]);
    }
  };

  return {
    ref,
    dateRange,
    setDateRange,
    userIds,
    serviceIds,
    print,
    usersServicesData,
    handleExport,
    selectAllUsers,
    selectAllServices,
    toggleServiceSelection,
    toggleResourceSelection,
    toggleUserSelection,
    modalOpened,
    setModalOpened,
    contentToPrint,
    biggerRowSize,
    setBiggerRowSize,
    selectAllResources,
    resourceIds,
  };
};
